import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import { useLocation } from "@reach/router"
import LinkButton from "components/Buttons/Link"
import Primary from "components/Buttons/Primary"
import { ReactComponent as LogoSVG } from "images/global/Logo.svg"
import { ReactComponent as ChevronSVG } from "images/global/chevronDown.svg"
import { loadPage } from "library/Loader/TransitionUtils"
import UniversalLink from "library/Loader/UniversalLink"
import { fresponsive } from "library/fullyResponsive"
import { pathnameMatches } from "library/functions"
import libraryConfig from "libraryConfig"
import { useState } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import { desktopBreakpoint } from "styles/media"
import textStyles from "styles/text"
import links from "utils/links"

export default function Header() {
	const pathname = useLocation().pathname
	const [solutionsOpen, setSolutionsOpen] = useState(false)
	const [useCasesOpen, setUseCasesOpen] = useState(false)

	const hideDemo = pathnameMatches(pathname, links.requestDemo)

	const solutionItems = [
		{ label: "Overview", href: links.solutions },
		{ label: "Impilo Platform", href: links.impiloPlatform },
		{ label: "Digital Health Logistics", href: links.digitalHealthLogistics },
		{ label: "Tech-Enabled Services", href: links.techEnabledServices },
		{ label: "Direct-to-Patient", href: links.directToPatient },
	]

	const useCaseItems = [
		{ label: "Virtual Care Companies", href: links.useCasesVirtualCare },
		{ label: "Physicians & Providers", href: links.useCasesPhysicians },
		{ label: "Health Plans & Payers", href: links.useCasesHealthPlans },
		{ label: "Health Systems & MSOs", href: links.useCasesHealthSystems },
		{ label: "Value-Based Care", href: links.useCasesValueBasedCare },
		{ label: "OEMs", href: links.useCasesOems },
	]

	const handleSolutionsItemClick = (href: string) => {
		setSolutionsOpen(false)
		loadPage(href, libraryConfig.defaultTransition).catch((error: string) => {
			throw new Error(error)
		})
	}

	const handleUseCasesItemClick = (href: string) => {
		setUseCasesOpen(false)
		loadPage(href, libraryConfig.defaultTransition).catch((error: string) => {
			throw new Error(error)
		})
	}

	return (
		<Wrapper>
			<Inner>
				<UniversalLink to={links.home}>
					<Logo />
				</UniversalLink>
				<Links>
					<DropdownMenu.Root
						open={solutionsOpen}
						onOpenChange={setSolutionsOpen}
						modal={false}
					>
						<DropdownTrigger>
							Our Solutions
							<ChevronIcon open={solutionsOpen} />
						</DropdownTrigger>
						<DropdownMenu.Portal>
							<DropdownContent sideOffset={8}>
								{solutionItems.map((item) => (
									<DropdownMenu.Item key={item.href} asChild>
										<DropdownItem
											onClick={() => handleSolutionsItemClick(item.href)}
										>
											{item.label}
										</DropdownItem>
									</DropdownMenu.Item>
								))}
							</DropdownContent>
						</DropdownMenu.Portal>
					</DropdownMenu.Root>
					<DropdownMenu.Root
						open={useCasesOpen}
						onOpenChange={setUseCasesOpen}
						modal={false}
					>
						<DropdownTrigger>
							Who We Serve
							<ChevronIcon open={useCasesOpen} />
						</DropdownTrigger>
						<DropdownMenu.Portal>
							<DropdownContent sideOffset={8}>
								{useCaseItems.map((item) => (
									<DropdownMenu.Item key={item.href} asChild>
										<DropdownItem
											onClick={() => handleUseCasesItemClick(item.href)}
										>
											{item.label}
										</DropdownItem>
									</DropdownMenu.Item>
								))}
							</DropdownContent>
						</DropdownMenu.Portal>
					</DropdownMenu.Root>
					<LinkButton to={links.about}>About Us</LinkButton>
					<LinkButton to={links.blog}>Blog</LinkButton>
					<LinkButton to={links.integration}>SDK</LinkButton>
					<LinkButton to={links.docs}>Docs</LinkButton>
					{!hideDemo && <Primary to={links.requestDemo}>Request Demo</Primary>}
				</Links>
			</Inner>
		</Wrapper>
	)
}

const Wrapper = styled.header`
  display: grid;
  place-items: center;
  height: auto;
  background: ${colors.blue05};
`

const Inner = styled.div`
  width: 100%;
  max-width: ${desktopBreakpoint}px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${fresponsive(css`
    padding: 50px 50px 0;
  `)}
`

const Logo = styled(LogoSVG)`
  height: auto;
  cursor: pointer;

  ${fresponsive(css`
    width: 69.3px;
    overflow: visible;
  `)}
`

const Links = styled.div`
  display: flex;
  align-items: center;

  ${fresponsive(css`
    gap: 40px;
  `)}
`

const ChevronIcon = styled(ChevronSVG)<{ open: boolean }>`
  width: 13px;
  height: auto;
  transition: transform 0.2s ease;
  transform: ${({ open }) => (open ? "rotate(180deg)" : "rotate(0deg)")};
`

const DropdownTrigger = styled(DropdownMenu.Trigger)`
  ${textStyles.bodyM};
  background: none;
  border: none;
  color: ${colors.silver04};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  transition: color 0.5s ease;

  &:hover {
    color: ${colors.brightGreen};
  }

  &:focus {
    outline: none;
  }
`

const DropdownContent = styled(DropdownMenu.Content)`
  background: ${colors.blue02};
  border: 1px solid ${colors.blue03};
  border-radius: 12px;
  padding: 8px 0;
  min-width: 200px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgb(0 0 0 / 15%);

  &[data-state="open"] {
    animation: slide-down 0.15s ease-out;
  }

  @keyframes slide-down {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const DropdownItem = styled.button`
  ${textStyles.bodyR};
  color: ${colors.silver05};
  padding: 12px 20px;
  display: block;
  text-decoration: none;
  transition: background 0.2s ease;
  cursor: pointer;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;

  &:hover {
    background: ${colors.blue01};
    color: ${colors.silver05};
  }

  &:focus {
    outline: none;
    background: ${colors.blue01};
  }
`
